import React, { useEffect, useState } from 'react';
import api from '../../api';
import {
    Container,
    Stack,
    Typography,
    Button,
    TextField,
    Card,
    TableRow,
    TableBody,
    TableContainer,
    Table,
    TableHead,
    TableCell,
    IconButton,
    MenuItem,
    Popover,
    Pagination,
    Select,
    FormControl,
    InputLabel,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Grid,
} from '@mui/material';
import { toast } from 'react-toastify';
import Iconify from '../iconify/Iconify';
import CircularProgressWithLabel from '../../utils/loader';

export const ENQUIRY_STATUS = [
    { label: 'Pending', value: 'pending' },
    { label: 'In Progress', value: 'in_progress' },
    { label: 'Resolved', value: 'resolved' },
    { label: 'Closed', value: 'closed' },
];

const INITIAL_PAGE_INFO = {
    page: 1,
    total_pages: 0,
    total: 0,
    current_page: 1,
};

const INITIAL_QUERY = {
    search: '',
    status: '',
};

const PER_PAGE = 10;

const formatDateTime = (value) => {
    if (!value) return '-';
    const date = new Date(value);
    if (Number.isNaN(date.getTime())) return '-';
    return date.toLocaleString();
};

const getStatusLabel = (value) => {
    const status = ENQUIRY_STATUS.find((item) => item.value === value);
    return status ? status.label : value || '-';
};

const normalizeListResponse = (data) => {
    const list =
        data?.enquiries ||
        data?.data ||
        data?.items ||
        [];

    const pageInfo =
        data?.pageInfo ||
        data?.pagination ||
        data?.meta ||
        {};

    const totalPages =
        pageInfo.total_pages ||
        pageInfo.totalPages ||
        pageInfo.last_page ||
        (pageInfo.total && PER_PAGE ? Math.ceil(pageInfo.total / PER_PAGE) : 0);

    const currentPage =
        pageInfo.current_page ||
        pageInfo.currentPage ||
        pageInfo.page ||
        1;

    return {
        list: Array.isArray(list) ? list : [],
        pageInfo: {
            ...INITIAL_PAGE_INFO,
            ...pageInfo,
            total_pages: totalPages || 1,
            current_page: currentPage,
        },
    };
};

const getEnquiryName = (row) =>
    row?.name ||
    row?.fullName ||
    row?.customerName ||
    '-';

const getEnquiryContact = (row) =>
    row?.contactNo ||
    row?.phone ||
    row?.phoneNumber ||
    row?.mobile ||
    '-';

const getEnquiryEmail = (row) => row?.email || '-';

const getEnquirySubject = (row) =>
    row?.subject ||
    row?.query ||
    row?.title ||
    '-';

const getEnquiryMessage = (row) =>
    row?.message ||
    row?.description ||
    row?.queryDetails ||
    '-';

export default function EnquiriesComponent() {
    const [open, setOpen] = useState(null);
    const [showEnquiries, setEnquiries] = useState([]);
    const [selectedEnquiry, setSelectedEnquiry] = useState(null);
    const [showLoader, setShowLoader] = useState(false);

    const [searchPageCount, setSearchPageCount] = useState(INITIAL_PAGE_INFO);
    const [searchQuery, setSearchQuery] = useState(INITIAL_QUERY);

    const [viewDialogOpen, setViewDialogOpen] = useState(false);
    const [viewLoading, setViewLoading] = useState(false);
    const [viewData, setViewData] = useState(null);

    const [updateDialogOpen, setUpdateDialogOpen] = useState(false);
    const [updateSaving, setUpdateSaving] = useState(false);
    const [updateForm, setUpdateForm] = useState({
        status: '',
        remarks: '',
    });

    const handleOpenMenu = (event) => {
        setOpen(event.currentTarget);
    };

    const handleCloseMenu = () => {
        setOpen(null);
    };

    const buildListParams = (page, query = searchQuery) => {
        const params = {
            page,
            per_page: PER_PAGE,
        };
        if (query.status) {
            params['filters[status]'] = query.status;
        }
        if (query.search) {
            params['filters[search]'] = query.search;
        }
        return params;
    };

    const getEnquiries = async (page = searchPageCount.page, queryOverride = null) => {
        const query = queryOverride || searchQuery;
        setShowLoader(true);
        try {
            const response = await api.get('/api/v1/enquiries', {
                params: buildListParams(page, query),
            });
            const data = response?.data || {};
            if (data.success === false) {
                throw new Error(data.message || 'Unable to load enquiries.');
            }
            const { list, pageInfo } = normalizeListResponse(data);
            setEnquiries(list);
            setSearchPageCount(pageInfo);
        } catch (error) {
            toast.error(error?.response?.data?.message || error?.message || 'Something went wrong!');
            setEnquiries([]);
        } finally {
            setShowLoader(false);
        }
    };

    useEffect(() => {
        getEnquiries(1);
    }, []);

    const viewMoreResult = (event, newPage) => {
        setSearchPageCount((prev) => ({ ...prev, page: newPage, current_page: newPage }));
        getEnquiries(newPage);
    };

    const onChangeSearch = (event) => {
        const { name, value } = event.target;
        setSearchQuery((prev) => ({ ...prev, [name]: value }));
    };

    const onClickSearch = () => {
        setSearchPageCount((prev) => ({ ...prev, page: 1, current_page: 1 }));
        getEnquiries(1);
    };

    const onClickReset = () => {
        setSearchQuery(INITIAL_QUERY);
        setSearchPageCount(INITIAL_PAGE_INFO);
        getEnquiries(1, INITIAL_QUERY);
    };

    const onClickAddEditData = (data) => {
        setSelectedEnquiry(data);
    };

    const fetchEnquiryDetail = async (id, options = {}) => {
        const { updateViewLoading = false } = options;
        if (updateViewLoading) {
            setViewLoading(true);
        }
        try {
            const response = await api.get(`/api/v1/enquiries/${id}`);
            const data = response?.data || {};
            if (data.success === false) {
                throw new Error(data.message || 'Unable to load enquiry details.');
            }
            const enquiry = data.enquiry || data.data || data;
            if (updateViewLoading) {
                setViewData(enquiry);
            }
            return enquiry;
        } catch (error) {
            toast.error(error?.response?.data?.message || error?.message || 'Unable to load enquiry details.');
            return null;
        } finally {
            if (updateViewLoading) {
                setViewLoading(false);
            }
        }
    };

    const onClickView = async () => {
        handleCloseMenu();
        if (!selectedEnquiry?.id) {
            toast.error('Enquiry not selected.');
            return;
        }
        setViewDialogOpen(true);
        setViewData(null);
        await fetchEnquiryDetail(selectedEnquiry.id, { updateViewLoading: true });
    };

    const onClickOpenUpdate = async () => {
        handleCloseMenu();
        if (!selectedEnquiry?.id) {
            toast.error('Enquiry not selected.');
            return;
        }
        setUpdateDialogOpen(true);
        setUpdateForm({
            status: selectedEnquiry.status || 'pending',
            remarks: selectedEnquiry.remarks || '',
        });
        const detail = await fetchEnquiryDetail(selectedEnquiry.id);
        if (detail) {
            setUpdateForm({
                status: detail.status || 'pending',
                remarks: detail.remarks || '',
            });
        }
    };

    const onChangeUpdateForm = (event) => {
        const { name, value } = event.target;
        setUpdateForm((prev) => ({ ...prev, [name]: value }));
    };

    const onClickSaveUpdate = async () => {
        if (!selectedEnquiry?.id) {
            toast.error('Enquiry not selected.');
            return;
        }
        if (!updateForm.status) {
            toast.error('Please select a status.');
            return;
        }

        setUpdateSaving(true);
        try {
            const response = await api.patch(`/api/v1/enquiries/${selectedEnquiry.id}`, {
                status: updateForm.status,
                remarks: updateForm.remarks,
            });
            const data = response?.data || {};
            if (data.success === false) {
                throw new Error(data.message || 'Failed to update enquiry.');
            }
            toast.success(data.message || 'Enquiry updated successfully.');
            setUpdateDialogOpen(false);
            getEnquiries(searchPageCount.current_page || 1);
        } catch (error) {
            toast.error(error?.response?.data?.message || error?.message || 'Failed to update enquiry.');
        } finally {
            setUpdateSaving(false);
        }
    };

    const renderDetailField = (label, value) => (
        <Grid item xs={12} sm={6}>
            <Typography variant="caption" color="text.secondary">
                {label}
            </Typography>
            <Typography variant="body2">{value || '-'}</Typography>
        </Grid>
    );

    const detailSource = viewData || selectedEnquiry;

    return (
        <>
            <Container maxWidth={false}>
                <Stack direction="row" alignItems="center" justifyContent="space-between" mb={5}>
                    <Typography variant="h4" gutterBottom>
                        Enquiries
                    </Typography>
                </Stack>

                <Card style={{ marginBottom: '30px' }}>
                    <Stack direction={{ xs: 'column', md: 'row' }} sx={{ my: 2 }} alignItems="center">
                        <TextField
                            id="outlined-basic"
                            label="Search"
                            name="search"
                            variant="outlined"
                            style={{ margin: '10px' }}
                            size="small"
                            fullWidth
                            placeholder="Search by name, email, phone..."
                            value={searchQuery.search}
                            onChange={onChangeSearch}
                        />
                        <FormControl fullWidth size="small" style={{ margin: '10px' }}>
                            <InputLabel id="enquiry-status-filter-label">Status</InputLabel>
                            <Select
                                labelId="enquiry-status-filter-label"
                                id="enquiry-status-filter"
                                label="Status"
                                name="status"
                                value={searchQuery.status}
                                onChange={onChangeSearch}
                            >
                                <MenuItem value="">All</MenuItem>
                                {ENQUIRY_STATUS.map((status) => (
                                    <MenuItem key={status.value} value={status.value}>
                                        {status.label}
                                    </MenuItem>
                                ))}
                            </Select>
                        </FormControl>
                        <Button variant="contained" style={{ margin: '10px' }} onClick={onClickSearch}>
                            Search
                        </Button>
                        <Button variant="outlined" style={{ margin: '10px' }} onClick={onClickReset}>
                            Reset
                        </Button>
                    </Stack>
                </Card>

                <Card>
                    {showLoader ? (
                        <CircularProgressWithLabel />
                    ) : (
                        <>
                            <TableContainer>
                                <Table stickyHeader aria-label="sticky table">
                                    <TableHead>
                                        <TableRow>
                                            <TableCell align="center">Id</TableCell>
                                            <TableCell align="center">Name</TableCell>
                                            <TableCell align="center">Email</TableCell>
                                            <TableCell align="center">Contact</TableCell>
                                            <TableCell align="center">Subject</TableCell>
                                            <TableCell align="center">Status</TableCell>
                                            <TableCell align="center">Remarks</TableCell>
                                            <TableCell align="center">Created On</TableCell>
                                            <TableCell />
                                        </TableRow>
                                    </TableHead>
                                    <TableBody>
                                        {showEnquiries.length > 0 &&
                                            showEnquiries.map((enquiry, index) => (
                                                <TableRow hover key={enquiry.id || index}>
                                                    <TableCell align="center">{enquiry.id}</TableCell>
                                                    <TableCell align="center">
                                                        <b>{getEnquiryName(enquiry)}</b>
                                                    </TableCell>
                                                    <TableCell align="center">{getEnquiryEmail(enquiry)}</TableCell>
                                                    <TableCell align="center">{getEnquiryContact(enquiry)}</TableCell>
                                                    <TableCell align="center">{getEnquirySubject(enquiry)}</TableCell>
                                                    <TableCell align="center">{getStatusLabel(enquiry.status)}</TableCell>
                                                    <TableCell align="center">{enquiry.remarks || '-'}</TableCell>
                                                    <TableCell align="center">
                                                        {formatDateTime(enquiry.createdAt || enquiry.created_at)}
                                                    </TableCell>
                                                    <TableCell align="right" onClick={() => onClickAddEditData(enquiry)}>
                                                        <IconButton size="large" color="inherit" onClick={handleOpenMenu}>
                                                            <Iconify icon={'eva:more-vertical-fill'} />
                                                        </IconButton>
                                                    </TableCell>
                                                </TableRow>
                                            ))}
                                        {showEnquiries.length === 0 && (
                                            <TableRow>
                                                <TableCell
                                                    colSpan={9}
                                                    align="center"
                                                    style={{ height: '200px', verticalAlign: 'middle' }}
                                                >
                                                    <Typography variant="h6" color="text.secondary">
                                                        No enquiries found
                                                    </Typography>
                                                </TableCell>
                                            </TableRow>
                                        )}
                                    </TableBody>
                                </Table>
                            </TableContainer>
                            {showEnquiries.length > 0 && (
                                <Stack alignItems="center">
                                    <Pagination
                                        color="primary"
                                        onChange={viewMoreResult}
                                        page={searchPageCount.current_page || 1}
                                        count={searchPageCount.total_pages || 1}
                                    />
                                </Stack>
                            )}
                            <Popover
                                open={Boolean(open)}
                                anchorEl={open}
                                onClose={handleCloseMenu}
                                anchorOrigin={{ vertical: 'top', horizontal: 'left' }}
                                transformOrigin={{ vertical: 'top', horizontal: 'right' }}
                                PaperProps={{
                                    sx: {
                                        p: 1,
                                        width: 160,
                                        '& .MuiMenuItem-root': {
                                            px: 1,
                                            typography: 'body2',
                                            borderRadius: 0.75,
                                        },
                                    },
                                }}
                            >
                                <MenuItem onClick={onClickView}>
                                    <Iconify icon={'eva:eye-fill'} sx={{ mr: 2 }} />
                                    View
                                </MenuItem>
                                <MenuItem onClick={onClickOpenUpdate}>
                                    <Iconify icon={'eva:edit-fill'} sx={{ mr: 2 }} />
                                    Update
                                </MenuItem>
                            </Popover>
                        </>
                    )}
                </Card>
            </Container>

            <Dialog open={viewDialogOpen} onClose={() => setViewDialogOpen(false)} fullWidth maxWidth="sm">
                <DialogTitle>Enquiry Details</DialogTitle>
                <DialogContent>
                    {viewLoading ? (
                        <CircularProgressWithLabel />
                    ) : (
                        <Grid container spacing={2} sx={{ mt: 0.5 }}>
                            {renderDetailField('Id', detailSource?.id)}
                            {renderDetailField('Name', getEnquiryName(detailSource))}
                            {renderDetailField('Email', getEnquiryEmail(detailSource))}
                            {renderDetailField('Contact', getEnquiryContact(detailSource))}
                            {renderDetailField('Subject', getEnquirySubject(detailSource))}
                            {renderDetailField('Status', getStatusLabel(detailSource?.status))}
                            {renderDetailField('Created On', formatDateTime(detailSource?.createdAt || detailSource?.created_at))}
                            {renderDetailField('Updated On', formatDateTime(detailSource?.updatedAt || detailSource?.updated_at))}
                            <Grid item xs={12}>
                                <Typography variant="caption" color="text.secondary">
                                    Message
                                </Typography>
                                <Typography variant="body2">{getEnquiryMessage(detailSource)}</Typography>
                            </Grid>
                            <Grid item xs={12}>
                                <Typography variant="caption" color="text.secondary">
                                    Remarks
                                </Typography>
                                <Typography variant="body2">{detailSource?.remarks || '-'}</Typography>
                            </Grid>
                        </Grid>
                    )}
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setViewDialogOpen(false)} variant="outlined">
                        Close
                    </Button>
                </DialogActions>
            </Dialog>

            <Dialog open={updateDialogOpen} onClose={() => setUpdateDialogOpen(false)} fullWidth maxWidth="sm">
                <DialogTitle>Update Enquiry</DialogTitle>
                <DialogContent>
                    <Stack spacing={2} sx={{ mt: 1 }}>
                        <FormControl fullWidth>
                            <InputLabel id="update-status-label">Status</InputLabel>
                            <Select
                                labelId="update-status-label"
                                label="Status"
                                name="status"
                                value={updateForm.status}
                                onChange={onChangeUpdateForm}
                            >
                                {ENQUIRY_STATUS.map((status) => (
                                    <MenuItem key={status.value} value={status.value}>
                                        {status.label}
                                    </MenuItem>
                                ))}
                            </Select>
                        </FormControl>
                        <TextField
                            label="Remarks"
                            name="remarks"
                            value={updateForm.remarks}
                            onChange={onChangeUpdateForm}
                            fullWidth
                            multiline
                            rows={4}
                            placeholder="Called customer and resolved the query."
                        />
                    </Stack>
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setUpdateDialogOpen(false)} variant="outlined" disabled={updateSaving}>
                        Cancel
                    </Button>
                    <Button onClick={onClickSaveUpdate} variant="contained" disabled={updateSaving}>
                        {updateSaving ? 'Saving...' : 'Save'}
                    </Button>
                </DialogActions>
            </Dialog>
        </>
    );
}
